import { useParams, Link, useNavigate, Outlet, useOutletContext, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { listPages, getSpace, PageRow, SpaceRow, SpacePolicyRow } from "../lib/api";
import SidebarPageList from "../components/SidebarPageList";
import GlobalHeader from "../components/GlobalHeader";
import { getCurrentUser } from "../lib/auth";

interface SpaceContext {
  pages: PageRow[];
  refreshPages: () => void;
  space: (SpaceRow & { policy: SpacePolicyRow }) | null;
  isOwner: boolean;
}

export function useSpaceContext() {
  return useOutletContext<SpaceContext>();
}

export default function SpaceLayout() {
  const params = useParams() as Record<string, string>;
  const username = params.username ?? "";
  const spaceSlug = params.spaceSlug ?? "";
  const currentPageSlug = params["*"] ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser<{ sub: string }>();
  const isSettingsPage = location.pathname === `/${username}/${spaceSlug}/settings`;

  const [pages, setPages] = useState<PageRow[]>([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [space, setSpace] = useState<(SpaceRow & { policy: SpacePolicyRow }) | null>(null);
  const isOwner = !!(currentUser?.sub && space && space.owner_user_id === currentUser.sub);

  function refreshPages() {
    if (!username || !spaceSlug) return;
    listPages(username, spaceSlug).then(setPages).catch(() => {});
  }

  useEffect(() => {
    if (!username || !spaceSlug) return;
    setPagesLoading(true);
    listPages(username, spaceSlug)
      .then(setPages)
      .catch(() => {})
      .finally(() => setPagesLoading(false));
    getSpace(username, spaceSlug).then((s) => setSpace(s)).catch(() => {});
  }, [username, spaceSlug]);

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-950">
      <GlobalHeader />

      {/* Space breadcrumb bar */}
      <div className="shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-2 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to={`/${username}`} className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">{username}</Link>
        <span>/</span>
        <Link to={`/${username}/${spaceSlug}`} className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
          {space?.name ?? spaceSlug}
        </Link>
        {isOwner && (
          <Link
            to={`/${username}/${spaceSlug}/settings`}
            title="Space settings"
            className={`ml-0.5 p-1 rounded transition-colors ${
              isSettingsPage
                ? "text-blue-600 dark:text-blue-400"
                : "text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400"
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-gray-200 dark:border-gray-800 flex flex-col overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-3 border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => navigate(`/${username}/${spaceSlug}/new`)}
              className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors text-center"
            >
              + New Page
            </button>
          </div>

          {pagesLoading && (
            <div className="p-3 space-y-2 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          )}

          {!pagesLoading && pages.length === 0 && (
            <p className="p-3 text-xs text-gray-400 dark:text-gray-600">No pages yet.</p>
          )}

          {!pagesLoading && pages.length > 0 && (
            <SidebarPageList
              pages={pages}
              username={username}
              spaceSlug={spaceSlug}
              currentSlug={currentPageSlug}
            />
          )}

          {isOwner && (
            <div className="mt-auto border-t border-gray-200 dark:border-gray-800 p-3">
              <Link
                to={`/${username}/${spaceSlug}/settings`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  isSettingsPage
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
            </div>
          )}
        </aside>

        {/* Content area — each child renders its own <main> */}
        <Outlet context={{ pages, refreshPages, space, isOwner } satisfies SpaceContext} />
      </div>
    </div>
  );
}
